import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import TextEditor from '../push/TextEditor';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { isImageFile } from '../../utils/ImageUtil';
import { API_BASE_URL } from '../../const/constants';
import { PURPLE, hexToRgba } from '../../utils/designUtils';

const pcInputStyle = {
    height: 42, borderRadius: 10, padding: '0 14px', width: '100%',
    border: '1.5px solid #ececf2', background: 'white',
    fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
};
const pcFocusOn = (e) => {
    e.target.style.borderColor = PURPLE;
    e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`;
};
const pcFocusOff = (e) => {
    e.target.style.borderColor = '#ececf2';
    e.target.style.boxShadow = 'none';
};

function PSection({ title, required, action, children }) {
    return (
        <section style={{ background: 'white', borderRadius: 16, border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '14px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {title}
                    {required && <span style={{ color: '#d97757', fontSize: 14 }}>*</span>}
                </h3>
                {action}
            </div>
            <div style={{ padding: '20px 22px' }}>{children}</div>
        </section>
    );
}

function PRow({ label, required, children, hint }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'flex-start', gap: 16, padding: '10px 0', borderBottom: '1px solid #f8f7fc' }}>
            <label style={{ paddingTop: 12, fontSize: 13, color: '#3a3849', fontWeight: 600 }}>
                {label}
                {required && <span style={{ color: '#d97757', marginLeft: 4 }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children}
                {hint && <span style={{ fontSize: 11.5, color: '#8b8a99' }}>{hint}</span>}
            </div>
        </div>
    );
}

function TemplatePhonePreview({ title, body, imgUrl }) {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #f0eff5', padding: '10px 14px', boxShadow: '0 1px 2px rgba(60,50,120,.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#8b8a99', fontWeight: 600, letterSpacing: '.04em' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#5BC2B2' }}>
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                    LIVE PREVIEW
                </div>
            </div>
            <div style={{ width: 320, height: 600, margin: '0 auto', borderRadius: 42, padding: 10, background: 'linear-gradient(160deg, #1c1a2e 0%, #2a2841 100%)', boxShadow: '0 30px 50px -20px rgba(20,15,40,.45), inset 0 0 0 2px rgba(255,255,255,.06), inset 0 2px 4px rgba(255,255,255,.08)', position: 'relative' }}>
                <span style={{ position: 'absolute', left: -2, top: 90, width: 3, height: 30, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', left: -2, top: 130, width: 3, height: 50, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', left: -2, top: 190, width: 3, height: 50, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', right: -2, top: 140, width: 3, height: 70, background: '#0f0d1c', borderRadius: 2 }} />
                <div style={{ width: '100%', height: '100%', borderRadius: 34, background: 'linear-gradient(180deg, #4a3f7a 0%, #5b8db8 35%, #8db5c8 70%, #c9b5a8 100%)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 26px 0', color: 'white', fontWeight: 600, fontSize: 13, position: 'relative' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{hh}:{mm}</span>
                        <span style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 100, height: 26, borderRadius: 14, background: '#0a0814' }} />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                                <rect x="0" y="6" width="2" height="4" rx=".5" fill="white" />
                                <rect x="3" y="4" width="2" height="6" rx=".5" fill="white" />
                                <rect x="6" y="2" width="2" height="8" rx=".5" fill="white" />
                                <rect x="9" y="0" width="2" height="10" rx=".5" fill="white" />
                            </svg>
                            <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
                                <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="white" strokeOpacity=".7" />
                                <rect x="2" y="2" width="14" height="6" rx="1" fill="white" />
                                <rect x="19.5" y="3.5" width="1.5" height="3" rx=".5" fill="white" fillOpacity=".7" />
                            </svg>
                        </span>
                    </div>
                    <div style={{ textAlign: 'center', color: 'white', marginTop: 28, textShadow: '0 1px 2px rgba(0,0,0,.20)' }}>
                        <div style={{ fontSize: 16, fontWeight: 500, opacity: .9 }}>
                            {new Date().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }).replace(/(\d+)월 (\d+)일 (.+요일)/, '$1월 $2일 $3')}
                        </div>
                        <div style={{ fontSize: 72, fontWeight: 200, letterSpacing: '-0.03em', lineHeight: 1.0, marginTop: -2 }}>{hh}:{mm}</div>
                    </div>
                    <div style={{ position: 'absolute', bottom: 100, left: 12, right: 12, background: 'rgba(255,255,255,.78)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 16, padding: '11px 12px', boxShadow: '0 4px 14px rgba(0,0,0,.10)', border: '0.5px solid rgba(255,255,255,.7)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <img src="/workitalk_icon_square.png" alt="App Icon" width={38} height={38} style={{ borderRadius: 9, flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,.10)', display: 'block', background: 'white' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1f1d2e', textTransform: 'uppercase', letterSpacing: '.02em' }}>WORKITALK</span>
                                <span style={{ fontSize: 11, color: 'rgba(0,0,0,.45)' }}>지금</span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e', marginTop: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis' }}>{title || '제목 없음'}</div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,.65)', lineHeight: 1.4, marginTop: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {body || '내용 없음'}
                            </div>
                            {imgUrl && <div style={{ marginTop: 6, height: 80, borderRadius: 8, background: `#e9e7ef url(${imgUrl}) center/cover no-repeat`, border: '1px solid rgba(0,0,0,.05)' }} />}
                        </div>
                    </div>
                    <span style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', width: 110, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.85)' }} />
                </div>
            </div>
        </div>
    );
}

export default function PushTemplateCreate() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useTokenStore();
    const isEditMode = location.pathname === '/admin/push/template/edit';
    const existingTemplate = location.state;
    const isAdmin = user?.currentRole === 'ADMIN';

    const [attachedFiles, setAttachedFiles] = useState([]);
    const [filePreviewUrls, setFilePreviewUrls] = useState({});

    const [templateData, setTemplateData] = useState({
        id: '',
        templateName: '',
        msg: '',
        extData: { iconUrl: '', imageUrl: '', body: '' },
        organizationCode: '',
    });

    const [templateList, setTemplateList] = useState([]);
    const [isTemplateUsed, setIsTemplateUsed] = useState(false);

    useEffect(() => {
        const loadTemplateList = async () => {
            try {
                const res = await compatApi.get('/admin/template/find/list');
                if (res.ok) setTemplateList(res.data.data || []);
            } catch (error) {
                console.error('템플릿 목록 로드 오류:', error);
            }
        };
        loadTemplateList();
    }, []);

    useEffect(() => {
        if (isEditMode && existingTemplate?.id) {
            loadTemplateData(existingTemplate.id);
            checkTemplateUsed(existingTemplate.id);
        }
    }, [isEditMode, existingTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

    const checkTemplateUsed = async (templateId) => {
        try {
            const response = await compatApi.get('/admin/template/isUsed/' + templateId);
            if (response.ok) setIsTemplateUsed(response.data);
        } catch (error) {
            console.error('템플릿 사용 여부 확인 오류:', error);
        }
    };

    const handleTemplateInfoChange = (e) => {
        const { name, value } = e.target;
        setTemplateData(prev => ({ ...prev, [name]: value }));
    };

    const loadTemplateData = async (templateId) => {
        try {
            const response = await compatApi.get('/admin/template/find/' + templateId + '?organizationCode=' + user.currentOrganizationCode);
            const template = response.data.data;
            template.extData = template.extData ? JSON.parse(template.extData) : {};
            if (template.extData?.imageUrl) {
                const fullImageUrl = template.extData.imageUrl.startsWith('http')
                    ? template.extData.imageUrl
                    : `${API_BASE_URL.replace('/api', '')}${template.extData.imageUrl}`;
                setFilePreviewUrls({ 'server_image': fullImageUrl });
            }
            setTemplateData(template);
        } catch (error) {
            console.log('템플릿 정보 로드 오류:', error);
            toast.error('템플릿 정보를 불러오는 중 오류가 발생했습니다.');
        }
    };

    const handleSaveTemplate = async () => {
        if (!isAdmin) { toast.error('ADMIN 권한이 필요합니다.'); return; }
        try {
            if (!templateData.templateName.trim()) { toast.error('템플릿 제목을 입력해주세요.'); return; }
            if (!templateData.msg?.trim()) { toast.error('메시지 제목을 입력해주세요.'); return; }
            if (!templateData.extData?.body?.trim()) { toast.error('메시지 내용을 입력해주세요.'); return; }

            const requestData = {
                ...templateData,
                organizationCode: user.currentOrganizationCode,
                extData: JSON.stringify(templateData.extData),
            };

            const response = isEditMode
                ? await compatApi.put('/admin/template/update', requestData)
                : await compatApi.post('/admin/template/create', requestData);

            if (response.ok) {
                if (!isEditMode && attachedFiles.length > 0) {
                    const savedTemplateId = response.data.data.id;
                    try {
                        const fileObj = attachedFiles[0];
                        const formData = new FormData();
                        formData.append('file', fileObj.file);
                        formData.append('testTemplateId', savedTemplateId);
                        const uploadResponse = await compatApi.upload('/admin/file/upload/testTemplate', formData);
                        if (uploadResponse.ok) {
                            const uploadedImageUrl = uploadResponse.data;
                            const extData = JSON.stringify({ ...templateData.extData, imageUrl: uploadedImageUrl });
                            await compatApi.put(`/admin/template/extData/${savedTemplateId}`, extData);
                            toast.success('템플릿과 이미지가 저장되었습니다.');
                        } else {
                            toast.error('이미지 업로드에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('이미지 업로드 오류:', error);
                        toast.warning('템플릿은 저장되었으나 이미지 업로드 중 오류가 발생했습니다.');
                    }
                } else {
                    toast.success(isEditMode ? '템플릿이 수정되었습니다.' : '템플릿이 생성되었습니다.');
                }
                navigate('/admin/push/template/list');
            } else {
                toast.error(isEditMode ? '템플릿 수정에 실패했습니다.' : '템플릿 생성에 실패했습니다.');
            }
        } catch (error) {
            if (error.response?.status === 400) alert(error.response.data);
            console.error('템플릿 저장 오류:', error);
            toast.error('템플릿 저장 중 오류가 발생했습니다.');
        }
    };

    const handleCancel = () => {
        if (isTemplateUsed) { navigate('/admin/push/template/list'); return; }
        if (window.confirm('작성 중인 내용이 저장되지 않습니다. 취소하시겠습니까?')) {
            navigate('/admin/push/template/list');
        }
    };

    const handleDelete = async () => {
        if (!isAdmin) { toast.error('ADMIN 권한이 필요합니다.'); return; }
        if (!isEditMode || !templateData.id) { toast.error('삭제할 템플릿이 없습니다.'); return; }
        if (!window.confirm('정말 이 템플릿을 삭제하시겠습니까?')) return;
        try {
            const response = await compatApi.del('/admin/template/delete/' + templateData.id + '?organizationCode=' + user.currentOrganizationCode, null);
            if (response.ok) {
                toast.success(templateData.templateName + ' 템플릿이 삭제되었습니다.');
                navigate('/admin/push/template/list');
            } else {
                toast.error('템플릿 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('템플릿 삭제 오류:', error);
            toast.error('템플릿 삭제 중 오류가 발생했습니다.');
        }
    };

    const handleTemplateExtData = (field, value) => {
        if (field === 'imageUrl' && value && value.startsWith('data:')) return;
        setTemplateData(prev => ({ ...prev, extData: { ...prev.extData, [field]: value } }));
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const maxSize = 1 * 1024 * 1024;
        if (file.size > maxSize) { toast.error('파일 크기는 1MB를 초과할 수 없습니다.'); e.target.value = null; return; }
        if (!isImageFile(file.name)) { toast.error('이미지 파일만 업로드 가능합니다.'); e.target.value = null; return; }

        if (isEditMode && templateData.id) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('testTemplateId', templateData.id);
                const response = await compatApi.upload('/admin/file/upload/testTemplate', formData);
                if (response.ok) {
                    handleTemplateExtData('imageUrl', response.data);
                    setPreviewUrls(response.data);
                    toast.success('이미지가 업로드되었습니다.');
                } else {
                    toast.error('이미지 업로드에 실패했습니다.');
                }
            } catch (error) {
                console.error('이미지 업로드 실패:', error);
                toast.error('이미지 업로드에 실패했습니다.');
            }
        } else {
            const timestamp = Date.now();
            const fileObj = { file, tempId: `temp_${timestamp}` };
            setAttachedFiles([fileObj]);
            const url = URL.createObjectURL(file);
            setFilePreviewUrls({ [`temp_${timestamp}`]: url });
            handleTemplateExtData('imageUrl', file.name);
            toast.success('이미지가 임시 저장되었습니다. 저장 시 업로드됩니다.');
        }
        e.target.value = null;
    };

    const setPreviewUrls = (url) => {
        if (!url) {
            setFilePreviewUrls(prev => { const { server_image, ...rest } = prev; return rest; });
            return;
        }
        const fullImageUrl = url.startsWith('http') ? url : `${API_BASE_URL.replace('/api', '')}${url}`;
        setFilePreviewUrls({ 'server_image': fullImageUrl });
    };

    const getPlainText = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const imagePreviewToPass = attachedFiles.length > 0
        ? filePreviewUrls[attachedFiles[0].tempId]
        : filePreviewUrls['server_image'] || null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ flex: 1, width: '100%', maxWidth: 1400, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px', boxSizing: 'border-box' }}>
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>관리자</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
                        <span>푸시</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>{isEditMode ? '템플릿 수정' : '템플릿 생성'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                {isEditMode ? '템플릿 수정' : '템플릿 생성'}
                            </h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                                자주 사용하는 알림 형식을 템플릿으로 등록하세요.
                            </p>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 20, alignItems: 'flex-start' }}>
                    {/* LEFT: form */}
                    <div>
                        <PSection title="템플릿 기본 정보">
                            <PRow label="템플릿 이름" required>
                                <input
                                    type="text"
                                    name="templateName"
                                    value={templateData.templateName}
                                    onChange={handleTemplateInfoChange}
                                    placeholder="템플릿 목록에 표시될 이름을 입력하세요."
                                    disabled={isTemplateUsed}
                                    style={pcInputStyle}
                                    onFocus={pcFocusOn}
                                    onBlur={pcFocusOff}
                                />
                            </PRow>
                        </PSection>

                        <TextEditor
                            key={templateData.id || 'new'}
                            initialContent={templateData.extData}
                            templateList={templateList}
                            title={templateData.msg}
                            onTitleChange={(value) => handleTemplateInfoChange({ target: { name: 'msg', value } })}
                            onTemplateSelect={loadTemplateData}
                            onExtDataChange={handleTemplateExtData}
                            handleImageChange={handleImageChange}
                            imagePreviewUrl={imagePreviewToPass}
                            setPreviewUrls={setPreviewUrls}
                            isLocked={isTemplateUsed}
                        />
                    </div>

                    {/* RIGHT: live preview */}
                    <div style={{ position: 'sticky', top: 24 }}>
                        <TemplatePhonePreview
                            title={templateData.msg}
                            body={getPlainText(templateData.extData?.body)}
                            imgUrl={imagePreviewToPass}
                        />
                    </div>
                </div>
            </main>

            <div style={{
                position: 'sticky', bottom: 0, left: 0, right: 0,
                background: 'rgba(255,255,255,.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                borderTop: '1px solid #ececf2',
                padding: '12px clamp(20px, 3vw, 40px)',
                display: 'flex', justifyContent: 'flex-end', gap: 8,
                zIndex: 20,
            }}>
                <button
                    onClick={handleCancel}
                    style={{ height: 40, padding: '0 18px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', color: '#5d5b6e', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                    {isTemplateUsed ? '닫기' : '취소'}
                </button>
                {isEditMode && !isTemplateUsed && (
                    <button
                        onClick={handleDelete}
                        style={{ height: 40, padding: '0 18px', borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                        삭제
                    </button>
                )}
                {!isTemplateUsed && (
                    <button
                        onClick={handleSaveTemplate}
                        style={{
                            height: 40, padding: '0 22px', borderRadius: 10, border: 'none',
                            background: `linear-gradient(135deg, ${PURPLE}, #5BC2B2)`,
                            color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`, display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                        저장
                    </button>
                )}
            </div>
        </div>
    );
}
